// Optional (dev|test|prod): bare ontrac.payrailz.com is production (same as payrailzprod.com).
const ONTRAC_HOST_PATTERN = /^([a-z0-9]*)ontrac\.payrailz(dev|test|prod)?\.com$/;
const LOCALHOST_ALLOWED_ENVS = ['dev1', 'dev2', 'dev3', 'qa1', 'qa2'];

/**
 * @param {unknown} subdomain
 * @returns {{ ok: false, reason: 'invalid' } | { ok: true, fullUrl: string } | { ok: true, slug: string }}
 */
function parseSubdomainInput(subdomain) {
  if (!subdomain || typeof subdomain !== 'string') {
    return { ok: false, reason: 'invalid' };
  }
  if (/^https?:\/\//i.test(subdomain)) {
    return { ok: true, fullUrl: subdomain };
  }
  return { ok: true, slug: subdomain };
}

/**
 * @param {string} [hostname]
 * @returns {string}
 */
function currentHostname(hostname) {
  return (
    hostname || (typeof window !== 'undefined' ? window.location.hostname : '')
  );
}

/**
 * @param {string} host
 * @returns
 *   | { kind: 'ontrac', env: string, railzType: 'dev' | 'test' | 'prod' }
 *   | { kind: 'localhost', env: string }
 *   | { kind: 'unknown' }
 */
function resolveIfpHostContext(host) {
  const match = host.match(ONTRAC_HOST_PATTERN);
  if (match) {
    return { kind: 'ontrac', env: match[1], railzType: match[2] || 'prod' };
  }
  if (host === 'localhost' || host === '127.0.0.1') {
    const clientEnv =
      typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_CLIENT_ENV;
    const env =
      clientEnv && LOCALHOST_ALLOWED_ENVS.includes(clientEnv)
        ? clientEnv
        : 'dev1';
    return { kind: 'localhost', env };
  }
  return { kind: 'unknown' };
}

/**
 * @param {string} slug
 * @param {'guest'|'enrolled'} variant
 * @param {{ kind: 'ontrac', env: string, railzType: 'dev'|'test'|'prod' } | { kind: 'localhost', env: string }} ctx
 * @returns {string}
 */
function buildUrlFromContext(slug, variant, ctx) {
  if (ctx.kind === 'localhost') {
    if (variant === 'guest') {
      return `https://${slug}.${ctx.env}loan.payrailzdev.com`;
    }
    return `https://${slug}.${ctx.env}pal-ifp.payrailzdev.com`;
  }

  const { env, railzType } = ctx;
  if (variant === 'guest') {
    return `https://${slug}.${env}loan.payrailz${railzType}.com`;
  }

  if (railzType === 'prod') {
    return `https://${slug}.loan.payrailzprod.com`;
  }
  if (railzType === 'test') {
    return `https://${slug}.${env}pal-ifp.payrailztest.com`;
  }
  return `https://${slug}.${env}pal-ifp.payrailzdev.com`;
}

/**
 * @param {string} subdomain
 * @param {string} [hostname]
 * @param {'guest'|'enrolled'} variant
 * @returns {string|null}
 */
function buildIfpUrl(subdomain, hostname, variant) {
  const parsed = parseSubdomainInput(subdomain);
  if (!parsed.ok) {
    return null;
  }
  if ('fullUrl' in parsed) {
    return parsed.fullUrl;
  }

  const host = currentHostname(hostname);
  const ctx = resolveIfpHostContext(host);
  if (ctx.kind === 'unknown') {
    return null;
  }

  return buildUrlFromContext(parsed.slug, variant, ctx);
}

/**
 * Builds a full IFP (In Front of PIN) v1 URL from a subdomain.
 *
 * Detects the environment from the current hostname (e.g. dev1ontrac.payrailzdev.com)
 * and constructs the full loan URL: https://{subdomain}.{env}loan.payrailz{type}.com
 *
 * Environment mapping:
 *   dev1, dev2, dev3, qa1, qa2 → {env}loan.payrailzdev.com
 *   imp1, uat1, conv           → {env}loan.payrailztest.com
 *   prod (empty prefix)        → loan.payrailzprod.com
 *
 * @param {string} subdomain - The custom FI text (e.g. "testbank123")
 * @param {string} [hostname] - Override for window.location.hostname (for testing)
 * @returns {string|null} Full URL or null if environment cannot be determined
 */
function buildPalIfpV1Url(subdomain, hostname) {
  return buildIfpUrl(subdomain, hostname, 'guest');
}

/**
 * Builds a full IFP (In Front of PIN) v2 URL from the same subdomain as guest Custom FI Text.
 *
 * Environment mapping:
 *   dev1, dev2, dev3, qa1, qa2 → {env}pal-ifp.payrailzdev.com
 *   imp1, uat1, conv           → {env}pal-ifp.payrailztest.com
 *   prod (empty prefix)        → loan.payrailzprod.com
 *
 * @param {string} subdomain - The custom FI text (e.g. "testbank123")
 * @param {string} [hostname] - Override for window.location.hostname (for testing)
 * @returns {string|null} Full URL or null if environment cannot be determined
 */
function buildPalIfpV2Url(subdomain, hostname) {
  return buildIfpUrl(subdomain, hostname, 'enrolled');
}

/**
 * @param {string} hostname
 * @returns {string|null}
 */
function derivePalIfpBffBaseFromBankHost(hostname) {
  const envMatch = hostname.match(
    /\.(dev\d|qa\d|imp\d|uat\d|conv)pal-ifp\.payrailz(dev|test|prod)\.com$/i
  );
  if (envMatch) {
    return `https://${envMatch[1]}api.payrailz${envMatch[2]}.com/pal-ifp-bff`;
  }
  if (/\.loan\.payrailzprod\.com$/i.test(hostname)) {
    return 'https://api.payrailzprod.com/pal-ifp-bff';
  }
  return null;
}

/**
 * @param {string} [hostname]
 * @returns {string|null}
 */
function derivePalIfpBffBaseFromHostContext(hostname) {
  const ctx = resolveIfpHostContext(currentHostname(hostname));
  if (ctx.kind === 'localhost') {
    return `https://${ctx.env}api.payrailzdev.com/pal-ifp-bff`;
  }
  if (ctx.kind === 'ontrac') {
    const prefix = ctx.env || '';
    return `https://${prefix}api.payrailz${ctx.railzType}.com/pal-ifp-bff`;
  }
  return null;
}

/**
 * @param {string} [assertionBaseUrl]
 * @param {string} [hostname]
 * @returns {string|null}
 */
function resolvePalIfpBffBase(assertionBaseUrl, hostname) {
  if (assertionBaseUrl && typeof assertionBaseUrl === 'string') {
    try {
      const normalized = assertionBaseUrl.startsWith('http')
        ? assertionBaseUrl
        : `https://${assertionBaseUrl}`;
      const fromBank = derivePalIfpBffBaseFromBankHost(new URL(normalized).hostname);
      if (fromBank) {
        return fromBank;
      }
    } catch (_e) {
      // fall through to host context
    }
  }
  return derivePalIfpBffBaseFromHostContext(hostname);
}

/**
 * Enrolled PAL IFP v2 assertion URL (pal-ifp-bff), not the bank IFP origin.
 *
 * @param {string} fspId
 * @param {string} [assertionBaseUrl] - bank PAL IFP base URL (used to infer API env)
 * @param {string} [hostname]
 * @returns {string|null}
 */
function buildEnrolledPalIfpBffAssertionUrl(fspId, assertionBaseUrl, hostname) {
  if (!fspId || !/^\d+$/.test(String(fspId))) {
    return null;
  }
  const bffBase = resolvePalIfpBffBase(assertionBaseUrl, hostname);
  if (!bffBase) {
    return null;
  }
  return `${bffBase.replace(/\/$/, '')}/api/v1/fsps/${fspId}/auth/assertion`;
}

const inFrontOfPinHelperExports = {
  buildPalIfpV1Url,
  buildPalIfpV2Url,
  buildEnrolledPalIfpBffAssertionUrl,
  derivePalIfpBffBaseFromBankHost,
  resolvePalIfpBffBase,
};

if (typeof module !== 'undefined' && module.exports) {
  module.exports = inFrontOfPinHelperExports;
}
if (typeof window !== 'undefined') {
  window.buildPalIfpV1Url = buildPalIfpV1Url;
  window.buildPalIfpV2Url = buildPalIfpV2Url;
  window.buildEnrolledPalIfpBffAssertionUrl = buildEnrolledPalIfpBffAssertionUrl;
}
